/* eslint-disable @typescript-eslint/class-literal-property-style */

import { Injectable, inject } from '@angular/core';
import { UserProfile, SignoutResponse, User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { BehaviorSubject, concat, from, Observable, of } from 'rxjs';
import { filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { JwtHelperService } from './jwt-decode';

export type IAuthenticationResult = SuccessAuthenticationResult | FailureAuthenticationResult | RedirectAuthenticationResult;

export enum AuthenticationResultStatus {
  Success,
  Redirect,
  Fail,
}

export interface SuccessAuthenticationResult {
  status: AuthenticationResultStatus.Success;
  state: any;
}

export interface FailureAuthenticationResult {
  status: AuthenticationResultStatus.Fail;
  message: string;
}

export interface RedirectAuthenticationResult {
  status: AuthenticationResultStatus.Redirect;
}

export interface IUser extends UserProfile {
  agencyNumber: string;
  lender: string;
  firmName: string;
  clientId: string;
  clubHub: string;
  fcaNumber: string;
  smartrCriteriaDisclaimerAccepted: string;
  smartrFitDisclaimerAccepted: string;
  marketingPreferencesSaved: string;
  role: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizeService {
  private readonly configService = inject(ConfigService);

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  private readonly popUpDisabled = true;

  private _userManagerPromise!: Promise<UserManager>;
  private readonly userSubject: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(null);
  private _user!: IUser;

  private get userManager(): Promise<UserManager> {
    if (this._userManagerPromise === undefined) {
      this._userManagerPromise = new Promise(async (resolve) => {
        const settings = {
          automaticSilentRenew: true,
          includeIdTokenInSilentRenew: true,
          checkSessionIntervalInSeconds: 5, // 5 seconds is the maximum as per the OIDC library.
        } as UserManagerSettings;

        const userManager = new UserManager(settings);

        userManager.events.addUserSignedOut(async () => {
          await userManager.removeUser();
          this.userSubject.next(null);
        });

        resolve(userManager);
      });
    }

    return this._userManagerPromise;
  }

  public isAuthenticated(): Observable<boolean> {
    return this.getUser().pipe(map((u: IUser | null) => !!u));
  }

  public getUser(): Observable<IUser | null> {
    return concat(
      this.userSubject.pipe(
        take(1),
        filter((u) => !!u),
        tap((u) => (this._user = u)),
      ),
      this.getUserFromStorage().pipe(
        filter((u: IUser) => !!u),
        tap((u) => {
          this._user = u;
          this.userSubject.next(u);
        }),
      ),
      this.userSubject.asObservable(),
    );
  }

  public get getUserInformation() {
    return this._user;
  }

  public setUser(user: IUser): Observable<IUser | null> {
    this.userSubject.next(user);
    return this.getUser();
  }

  get allowSmartrCriteriaPlus() {
    return true;
  }

  get user(): IUser {
    return this._user;
  }

  get tokenKey() {
    return `oidc.user:${this.configService.config.identityServerUrl}:${this.configService.applicationName}`;
  }

  get tokenEndpoint() {
    return `${this.configService.config.identityServerUrl}/connect/token`;
  }

  get isSsoUser(): Observable<boolean> {
    return this.getUser().pipe(map(() => this._user && this._user.sso_user === 'true'));
  }

  get isS365Client(): boolean {
    return this._user?.client_id === 'Smartr365';
  }

  public hasRole(role: string): boolean {
    return this._user?.role && (Array.isArray(this._user.role) ? this._user.role.some((x) => x === role) : this._user.role === role);
  }

  public getAccessToken(): Observable<string> {
    return from(this.userManager).pipe(
      mergeMap((userManager) => from(userManager.getUser())),
      map((user) => user?.access_token || ''),
    );
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  public async signIn(state: any): Promise<IAuthenticationResult> {
    const userManager = await this.userManager;
    let user: User | null = null;
    try {
      user = await userManager.signinSilent(this.createArguments());
      this.userSubject.next(user.profile as IUser);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      // eslint-disable-next-line no-console
      console.log('Silent authentication error: ', silentError);

      try {
        if (this.popUpDisabled) {
          throw new Error('Popup disabled. Change \'authorize.service.ts:AuthorizeService.popupDisabled\' to false to enable it.');
        }
        user = await userManager.signinPopup(this.createArguments());
        this.userSubject.next(user.profile as IUser);
        return this.success(state);
      } catch (popupError: any) {
        if ((popupError as Error).message === 'Popup window closed') {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error('The user closed the window.');
        } else if (!this.popUpDisabled) {
          // eslint-disable-next-line no-console
          console.log('Popup authentication error: ', popupError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await userManager.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError: any) {
          // eslint-disable-next-line no-console
          console.log('Redirect authentication error: ', redirectError);
          return this.error(redirectError);
        }
      }
    }
  }

  public async completeSignIn(url: string): Promise<IAuthenticationResult> {
    try {
      const userManager = await this.userManager;
      const user = await userManager.signinCallback(url);
      this.userSubject.next(user && (user.profile as IUser));
      return this.success(user && user.state);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('There was an error signing in: ', error);
      return this.error('There was an error signing in.');
    }
  }

  public async signOut(state: any): Promise<IAuthenticationResult> {
    const userManager = await this.userManager;
    try {
      if (this.popUpDisabled) {
        throw new Error('Popup disabled. Change \'authorize.service.ts:AuthorizeService.popupDisabled\' to false to enable it.');
      }
      await userManager.signoutPopup(this.createArguments());
      this.userSubject.next(null);
      return this.success(state);
    } catch (popupSignOutError) {
      // eslint-disable-next-line no-console
      console.log('Popup signout error: ', popupSignOutError);
      try {
        await userManager.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError: any) {
        // eslint-disable-next-line no-console
        console.log('Redirect signout error: ', popupSignOutError);
        return this.error(redirectSignOutError);
      }
    }
  }

  public async completeSignOut(url: string): Promise<IAuthenticationResult> {
    const userManager = await this.userManager;
    try {
      const state: SignoutResponse | any = await userManager.signoutCallback(url);
      this.userSubject.next(null);
      sessionStorage.clear();
      return this.success(state && state.data);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error);
    }
  }

  private createArguments(state?: any): any {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return { redirectMethod: 'replace', state_data: state };
  }

  private error(message: string): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  private success(state: any): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Success, state };
  }

  private redirect(): IAuthenticationResult {
    return { status: AuthenticationResultStatus.Redirect };
  }

  private getUserFromStorage(): Observable<IUser> {
    return from(this.userManager).pipe(
      mergeMap((um) => um.getUser()),
      mergeMap(() => this.getExternalUser()),
      map((u) => u && u.profile),
    );
  }

  private getExternalUser(): Observable<any> {
    const tokenString = sessionStorage.getItem(this.tokenKey);
    const offlineToken = tokenString ? JSON.parse(tokenString) : null;
    if (offlineToken) {
      const service = new JwtHelperService();
      const profile = service.decodeToken(offlineToken.access_token);
      profile.email = Object.values(profile).find((value: any) => /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(value.toString()));
      return of({ profile });
    }
    return of(null);
  }
}
